import { DateTime } from 'luxon';
import { css, html, LitEntity, SirenActionMixin, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import './bin-path-name';
import './bin-profile';
import '../graphs/bin-level/bin-level-smooth-history-graph';

class BinLevelsAdminListItem extends SirenActionMixin(LitEntity) {
  constructor() {
    super();

    this.start = DateTime.utc().minus({ weeks: 3 }).startOf('day').toISO();
    this.end = DateTime.utc().plus({ weeks: 1 }).endOf('day').toISO();
  }

  static get properties() {
    return {
      bins: { type: Array, attribute: false },
      sensorType: { type: String, attribute: false },
      printMode: { type: Boolean, attribute: 'print-mode', reflect: true },
      hasComment: { type: Boolean, attribute: 'has-comment', reflect: true },
      showGraphToolbar: { type: Boolean, attribute: 'show-graph-toolbar', reflect: true },
      showRaw: { type: Boolean, attribute: 'show-raw' },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        background-color: white;
        padding: var(--root-margin);
        margin: var(--root-margin);
        page-break-inside: avoid;
      }
      .parent {
        position: relative;
      }
      .parent-title {
        margin: var(--root-margin);
        font-weight: bold;
      }
      :host([print-mode]) .parent-title {
        position: absolute;
        left: 50%;
        top: 0;
        margin: 0;
      }
      bin-profile {
        height: 190px;
        max-width: 85px;
      }
      .graph {
        height: 215px;
        width: 80%;
      }
      :host([show-graph-toolbar]) .graph {
        height: auto;
      }
      textarea {
        height: 170px;
        width: 20%;
      }
      .item-inner {
        display: flex;
      }
      .sensor-type-label {
        margin-left: .5rem;
        font-size: 0.9em;
      }
      :host([print-mode]) {
        width: 195mm;
        padding: 0;
        margin: 0 0 var(--root-margin) 0;
      }
      :host([print-mode]) .graph {
        width: calc(100% - 110px);
        height: 155px;
      }
      :host([print-mode]) bin-profile {
        height: 125px;
      }
      :host([print-mode]) .graph,
      :host([print-mode]) bin-profile {
        display: inline-block;
      }
      :host([print-mode]) .item-inner {
        display: block;
        width: 100%;
      }
      :host([print-mode]) .item-inner textarea {
        width: calc(100% - var(--root-margin));
        margin: calc(var(--root-margin) / 2);
      }
      @media print {
        /* Flex display breaks page-break css attributes */
        .item-inner {
          display: flex;
          align-items: center;
        }
        a {
          text-decoration: none;
        }
        textarea {
          display: none;
        }
        :host([has-comment]) textarea {
          height: 140px;
          display: block;
        }
      }
    `];
  }

  renderEntity() {
    if (this.entity.properties && this.entity.properties.isAggregate) {
      const childBins = this.entity.getSubEntitiesByRel('https://api.binsentry.com/rel/bin-aggregate-child');
      return html`
        <div class="parent">
          <h3 class="parent-title">${this.entity.properties.name}</h3>
          ${childBins.map((bin) => html`
            <bin-levels-admin-list-item
              .href="${bin.href}"
              ?print-mode="${this.printMode}"
              ?show-graph-toolbar="${this.showGraphToolbar}"
              ?show-raw="${this.showRaw}"
            ></bin-levels-admin-list-item>
          `)}
        </div>
      `;
    }

    const deviceHref = this.entity?.getSubEntityByRel('https://api.binsentry.com/rel/device')?.href;

    return html`
      <h4>
        <!-- Target _blank intentional here as this page does not save entered info -->
        <a target="_blank" href="/bins/show/overview?binId=${encodeURIComponent(this.href)}">${this.entity.properties.name}</a>
        ${renderIf(deviceHref, () => html`<span class="subdued sensor-type-label">(<device-sensor-type .href=${deviceHref}></device-sensor-type>)</span>`)}
      </h4>
      <bin-path-name .href="${this.href}"></bin-path-name>
      <div class="item-inner">
        <bin-profile .href="${this.href}"></bin-profile>
        <bin-level-smooth-history-graph
          class="graph"
          heightOverride="${!this.showGraphToolbar ? 'calc(100% - 1ex)' : ''}"
          ?hide-toolbar="${!this.showGraphToolbar}"
          .href="${this.href}"
          .start="${this.start}"
          .end="${this.end}"
          ?forecast="${true}"
          ?show-raw="${this.showRaw}"
          ?hide-options="${this.showRaw}"
        ></bin-level-smooth-history-graph>
        <textarea @keyup="${(e) => this._keyUp(e)}"></textarea>
      </div>
    `;
  }

  _keyUp(e) {
    const hasComment = e.target.value !== '';
    if (hasComment !== this.hasComment) {
      this.hasComment = hasComment;
    }
  }

  updated(changedProperties) {
    if (!changedProperties.has('printMode')) {
      return;
    }

    const graphs = this.shadowRoot.querySelectorAll('bin-level-smooth-history-graph');
    for (const graph of graphs) {
      graph.onResize();
    }
  }
}

window.customElements.define('bin-levels-admin-list-item', BinLevelsAdminListItem);
